<template>
  <div class="landing-screen">
    <div class="claim-hero tw-text-white">
      <div class="hero-content">
        <img class="hero-logo" src="@/assets/header.png" alt="APA" />
        <div class="hero-kicker">Motor Accident Report</div>
        <h1>Let's sort your motor claim.</h1>
        <p>
          Take a breath — we'll walk with you. Submit your accident report,
          policy details, photos and documents in one secure place.
        </p>

        <div class="hero-support">
          <span class="hero-support-icon">
            <v-icon color="white" small>mdi-headset</v-icon>
          </span>
          <p>
            Need help while filling this? Call <b>0709 912 777</b>. APA claims
            support is available 24/7.
          </p>
        </div>
      </div>

      <div class="hero-panel">
        <div class="panel-card panel-card-main">
          <v-icon color="primary">mdi-shield-check-outline</v-icon>
          <div>
            <strong>Secure submission</strong>
            <span>Encrypted details, photos and documents.</span>
          </div>
        </div>
        <div class="panel-card">
          <v-icon color="secondary">mdi-camera-outline</v-icon>
          <div>
            <strong>Photo guided</strong>
            <span>Clear prompts for every vehicle angle.</span>
          </div>
        </div>
        <div class="panel-card">
          <v-icon color="primary">mdi-clipboard-check-outline</v-icon>
          <div>
            <strong>Validated steps</strong>
            <span>Required fields are checked as you go.</span>
          </div>
        </div>
      </div>
    </div>

    <div class="landing-body">
      <div class="prepared-card">
        <v-icon color="secondary" class="tw-flex-none">
          mdi-information-outline
        </v-icon>
        <div>
          <strong>Before you begin</strong>
          <p>
            Have your policy number, police details and vehicle photos ready.
            You reached this page by scanning an APA assessment centre QR code
            or through a secure claim link.
          </p>
        </div>
      </div>

      <div class="journeys">
        <button
          type="button"
          class="journey journey--primary"
          data-testid="start-claim"
          @click="$emit('start')"
        >
          <span class="journey-icon">
            <v-icon color="white">mdi-clipboard-text-outline</v-icon>
          </span>
          <span class="journey-copy">
            <strong>Submit a claim form</strong>
            <span>
              Report a claim and get a claim number &mdash; guided form, upload
              or voice.
            </span>
          </span>
          <v-icon class="journey-chevron">mdi-chevron-right</v-icon>
        </button>

        <button
          type="button"
          class="journey"
          data-testid="start-windscreen"
          @click="$emit('start-windscreen')"
        >
          <span class="journey-icon">
            <v-icon color="primary">mdi-car-windshield-outline</v-icon>
          </span>
          <span class="journey-copy">
            <strong>Windscreen claim</strong>
            <span>
              Cracked or chipped windscreen? We&rsquo;ll send an approved fitter
              to you.
            </span>
          </span>
          <v-icon class="journey-chevron">mdi-chevron-right</v-icon>
        </button>

        <button
          type="button"
          class="journey"
          data-testid="start-resume"
          @click="$emit('start-resume')"
        >
          <span class="journey-icon">
            <v-icon color="primary">mdi-progress-clock</v-icon>
          </span>
          <span class="journey-copy">
            <strong>Resume with Reference</strong>
            <span>
              Already started a claim? Enter your reference to pick up where you
              left off.
            </span>
          </span>
          <v-icon class="journey-chevron">mdi-chevron-right</v-icon>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LandingScreen',
  emits: ['start', 'start-windscreen', 'start-resume'],
}
</script>

<style scoped>
.landing-screen {
  min-height: calc(100vh - 48px);
  display: grid;
  /* proportional right column so the cards flex with the wider shell
     instead of staying pinned at 420px */
  grid-template-columns: minmax(0, 1.5fr) minmax(400px, 1fr);
}

.claim-hero {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 0;
  min-height: 620px;
  padding: 56px;
  overflow: hidden;
  background: linear-gradient(
      135deg,
      rgba(17, 76, 172, 0.96),
      rgba(3, 78, 162, 0.96)
    ),
    url('~@/assets/header.png');
  background-size: auto, 360px;
  background-repeat: no-repeat;
  background-position: center, 12% 12%;
}

.claim-hero::after {
  content: '';
  position: absolute;
  right: -120px;
  bottom: -160px;
  width: 420px;
  height: 420px;
  border: 76px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;
}

.hero-content,
.hero-panel {
  position: relative;
  z-index: 1;
  width: 100%;
  min-width: 0;
}

.hero-logo {
  width: 116px;
  height: auto;
  margin-bottom: 44px;
}

.hero-kicker {
  margin-bottom: 12px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
  opacity: 0.82;
}

.claim-hero h1 {
  max-width: 560px;
  margin: 0;
  font-size: 48px;
  font-weight: 800;
  line-height: 1.08;
  overflow-wrap: break-word;
}

.claim-hero p {
  max-width: 560px;
  margin: 22px 0 0;
  font-size: 20px;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.9);
}

.hero-panel {
  display: none;
}

/* the two claim journeys, offered as the same pair of cards at every width */
.journeys {
  display: grid;
  gap: 12px;
}

.journey {
  display: flex;
  gap: 16px;
  align-items: center;
  width: 100%;
  padding: 20px;
  border: 1px solid #e4e9f1;
  border-radius: 18px;
  background: #fff;
  text-align: left;
  box-shadow: 0 10px 26px rgba(17, 76, 172, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.journey:hover {
  transform: translateY(-2px);
  box-shadow: 0 18px 40px rgba(17, 76, 172, 0.16);
}

.journey--primary {
  border-color: #1b2f6b;
  background: #1b2f6b;
  box-shadow: 0 18px 40px rgba(17, 76, 172, 0.28);
}

.journey-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex: none;
  border-radius: 14px;
  background: #edf4ff;
}

.journey--primary .journey-icon {
  background: #33468b;
}

.journey-copy {
  flex: 1;
  min-width: 0;
}

.journey-copy strong {
  display: block;
  color: #14213d;
  font-size: 19px;
  font-weight: 800;
}

.journey-copy span {
  display: block;
  margin-top: 4px;
  color: #4d4d4e;
  font-size: 14px;
  line-height: 1.5;
}

.journey--primary .journey-copy strong {
  color: #fff;
}

.journey--primary .journey-copy span {
  color: #dbe3f5;
}

/* compound with .v-icon so the scoped selector out-specifies Vuetify's
   .theme--light.v-icon colour without needing !important */
.journey-chevron.v-icon {
  flex: none;
  color: #9db1cc;
}

.journey--primary .journey-chevron.v-icon {
  color: #fff;
}

.panel-card {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 18px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 18px 45px rgba(3, 32, 78, 0.18);
}

.panel-card-main {
  transform: translateX(-20px);
}

.panel-card strong,
.panel-card span {
  display: block;
}

.panel-card strong {
  color: #14213d;
  font-size: 15px;
}

.panel-card span {
  margin-top: 3px;
  color: #5d6475;
  font-size: 13px;
  line-height: 1.4;
}

.landing-body {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 22px;
  min-width: 0;
  padding: 56px 44px;
}

.prepared-card {
  display: flex;
  gap: 14px;
  padding: 18px;
  border: 1px solid #fde4a8;
  border-radius: 16px;
  background: #fff9eb;
}

.prepared-card strong {
  color: #24324b;
}

.prepared-card p {
  margin: 4px 0 0;
  color: #4d4d4e;
  font-size: 14px;
  line-height: 1.55;
}

.hero-support {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  max-width: 560px;
  margin: 28px 0 0;
  padding: 16px 18px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.1);
}

.hero-support-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex: none;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
}

.hero-support p {
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  line-height: 1.5;
}

.hero-support p b {
  color: #fff;
  white-space: nowrap;
}

@media (max-width: 1024px) {
  .landing-screen {
    display: block;
  }

  .claim-hero {
    min-height: auto;
    grid-template-columns: minmax(0, 1fr);
    padding: 42px;
  }

  .claim-hero h1 {
    max-width: 680px;
    font-size: 46px;
  }

  .hero-panel {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .panel-card-main {
    transform: none;
  }

  .landing-body {
    padding: 34px 42px 42px;
  }
}

@media (max-width: 640px) {
  .landing-screen {
    min-height: 100vh;
  }

  .claim-hero {
    padding: 28px 22px 30px;
  }

  .hero-logo {
    width: 88px;
    margin-bottom: 34px;
  }

  .claim-hero h1 {
    font-size: 33px;
    line-height: 1.14;
  }

  .claim-hero p {
    font-size: 15px;
  }

  .hero-support {
    margin-top: 20px;
    padding: 14px;
  }

  .hero-support p {
    font-size: 13px;
  }

  .hero-panel {
    display: none;
  }

  .landing-body {
    padding: 24px 20px 32px;
  }

  .journey {
    gap: 14px;
    padding: 16px;
    border-radius: 16px;
  }

  .journey-icon {
    width: 42px;
    height: 42px;
    border-radius: 12px;
  }

  .journey-copy strong {
    font-size: 16px;
  }

  .journey-copy span {
    font-size: 13px;
  }

  .prepared-card {
    border-radius: 12px;
  }
}
</style>
